import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useEffect, useRef, useState } from "react";

const usePrint = (filename: string) => {
  const currentRef = useRef<HTMLDivElement | null>(null);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  useEffect(() => {
    if (currentRef.current) {
      const images = currentRef.current.querySelectorAll("img");
      const imagePromises = Array.from(images).map((img) => {
        return new Promise<void>((resolve, reject) => {
          if (img.complete) {
            resolve();
          } else {
            img.onload = () => resolve();
            img.onerror = reject;
          }
        });
      });

      Promise.all(imagePromises)
        .then(() => setImagesLoaded(true))
        .catch((error) => console.error("Error loading images:", error));
    }
  }, []);

  const downloadPDF = async () => {
    if (!currentRef.current || !imagesLoaded) {
      console.error("Content reference is undefined or images are not loaded");
      return;
    }

    const canvas = await html2canvas(currentRef.current, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
    });
    const imgWidth = 210; // A4 width in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });
    const pageHeight = pdf.internal.pageSize.getHeight();
    let yPosition = 0;

    while (yPosition < imgHeight) {
      const pageCanvas = document.createElement("canvas");
      const pageCtx = pageCanvas.getContext("2d");

      pageCanvas.width = canvas.width;
      pageCanvas.height = (pageHeight * canvas.width) / imgWidth; // Convert mm to px

      if (pageCtx) {
        pageCtx.drawImage(
          canvas,
          0,
          (yPosition * canvas.width) / imgWidth, // Crop Y position
          canvas.width,
          pageCanvas.height,
          0,
          0,
          canvas.width,
          pageCanvas.height
        );

        const imgData = pageCanvas.toDataURL("image/png");
        pdf.addImage(
          imgData,
          "PNG",
          0,
          0,
          imgWidth,
          (pageCanvas.height * imgWidth) / canvas.width
        );
        yPosition += pageHeight;

        if (yPosition < imgHeight) pdf.addPage();
      }
    }

    pdf.save(filename);
  };

  return { currentRef, downloadPDF };
};

export default usePrint;
